import type { Gender, ScanConfigResponse } from '../api/types'

/**
 * What a scan calibrates with when the assessment carries nothing usable.
 *
 * **Not a preference — a floor under a field that is not required to arrive.** `Age` on
 * `CreateVitalsAssessmentRequest` is an optional `int`, so a caller that omits it mints an
 * assessment whose `ScanConfigResponse` says `age: 0`, and `Gender` defaults to `Unknown` the same
 * way. Both are handed to the vendor as calibration for BLOOD PRESSURE, which is the one vital this
 * whole flow exists to collect: Mindset drops BP from a session with no age and gender at all, and
 * an age of zero is worse than that — it is a number the model will believe.
 *
 * 60/male is the mid-point of the population these scans are issued to rather than a measurement,
 * and it is deliberately the same pair `toSdkSex`/`toMindsetGender` already fall back to for
 * `Unknown`, so the two vendors calibrate a demographics-less scan identically.
 */
export const FALLBACK_AGE = 60

/** The other half of the pair above. `Unknown` already lands here; a missing value now does too. */
export const FALLBACK_GENDER: Gender = 'Male'

/**
 * The bounds an age has to fall inside to be believed, which is the whole test applied to it.
 *
 * Wide on purpose. This is not validating a patient record — that happened wherever the assessment
 * was created — it is deciding whether the number on the wire is a real answer or the absence of
 * one, and the absence has exactly one shape (`0`, the `int` default). The ceiling is here for the
 * other direction: a garbled or out-by-a-magnitude value calibrates the vendor's model just as
 * confidently as a good one, and silently.
 */
const YOUNGEST = 1
const OLDEST = 120

/**
 * The age this scan calibrates with: the assessment's, when it sent a usable one.
 *
 * Fractions are rounded rather than refused (the vendor's `data.user.age` is an integer), and
 * everything else — absent, zero, negative, NaN, out of the window above, or not a number at all
 * because this arrives from an unvalidated `as ScanConfigResponse` cast — falls back.
 */
export function calibrationAge(age: number | null | undefined): number {
  if (typeof age !== 'number' || !Number.isFinite(age)) {
    return FALLBACK_AGE
  }
  const rounded = Math.round(age)
  return rounded >= YOUNGEST && rounded <= OLDEST ? rounded : FALLBACK_AGE
}

/** The gender this scan calibrates with. Anything but the two the platform names falls back. */
export function calibrationGender(gender: Gender | null | undefined): Gender {
  return gender === 'Female' || gender === 'Male' ? gender : FALLBACK_GENDER
}

/**
 * The pair, read off the scan config the service answered `GET /v1/public/scan` with.
 *
 * One function rather than two reads at each call site, because both vendors need both values and
 * the point is that a scan is calibrated from the ASSESSMENT rather than from a constant: the age
 * and gender were resolved wherever the patient record lives, travelled on the create request, and
 * this is where they are used. The fallbacks exist for the assessment that carried neither.
 */
export function scanCalibration(config: ScanConfigResponse): { age: number; gender: Gender } {
  return { age: calibrationAge(config.age), gender: calibrationGender(config.gender) }
}

/**
 * The SDK's `sex` parameter is binary (0 female, 1 male — fhvitals_sdk.js JSDoc); the platform's
 * Gender vocabulary carries a third value, Unknown. faceheart2.html's own demo defaults anything
 * that isn't 'female' to male, and this app inherits that rather than inventing a better answer the
 * vendor's calibration model was never given a third option to use — see docs/decisions.md.
 */
export function toSdkSex(gender: Gender): 0 | 1 {
  return gender === 'Female' ? 0 : 1
}

/**
 * The same question for the other vendor, whose vocabulary is `'M' | 'F'` and which uses it to
 * ENABLE blood pressure rather than only to calibrate it - the SDK drops BP from a session that
 * carries no age and gender.
 *
 * `Unknown` therefore lands on the same inherited default `toSdkSex` uses, and for a sharper reason:
 * mapping it to "no gender" would not produce a more honest reading, it would produce a scan with no
 * blood pressure in it, which is the one vital this whole flow exists to collect.
 */
export function toMindsetGender(gender: Gender): 'M' | 'F' {
  return gender === 'Female' ? 'F' : 'M'
}

/**
 * An `<input type="date">`'s `.value` is spec-guaranteed `yyyy-MM-dd` regardless of the browser's
 * display locale (HTML Standard §input.date), which is the one format Auth's `verify` and this
 * platform's `LinkGrantFactorHash.OfDateOfBirth` agree on (laennec-libraries
 * LinkGrantFactorHash.cs: "the feature's own page is responsible for submitting yyyy-MM-dd"). A
 * hand-formatted date risks exactly the two-ends-disagree failure that overload's own doc comment
 * warns about, so this validates the native input's value rather than reformatting it.
 */
export function isWellFormedIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false
  }
  const [year, month, day] = value.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day))
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  )
}
